import React, { useEffect, useState } from "react";
import { Box, Typography, TextField, Button, Paper, CircularProgress } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useIntl } from "react-intl";
import { fetchCompany, saveCompanyValues } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { COMPANY_ID } from "../../config/appConfig";

export default function CompanyValuesPage() {
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCompany();
  }, []);

  const loadCompany = async () => {
    setLoading(true);
    try {
      const data = await fetchCompany(COMPANY_ID);
      setCompany(data);
      setDraft(data.purpose || "");
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setDraft(company?.purpose || "");
    setEditing(true);
  };

  const handleCancel = () => {
    setDraft(company?.purpose || "");
    setEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveCompanyValues(COMPANY_ID, draft);
      notify.success(t("message.update.success"));
      setCompany({ ...company, purpose: draft });
      setEditing(false);
    } catch (e) {
      notify.apiError(e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {t("companyValues.title")}
        </Typography>
        {!editing && (
          <Button variant="contained" startIcon={<EditIcon />} onClick={handleEdit}>
            {t("common.edit")}
          </Button>
        )}
      </Box>

      {company && (
        <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {company.companyName}
          </Typography>

          {editing ? (
            <>
              <TextField
                fullWidth
                multiline
                rows={10}
                label={t("companyValues.label")}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {t("common.save")}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                  disabled={saving}
                >
                  {t("common.cancel")}
                </Button>
              </Box>
            </>
          ) : (
            <Typography
              variant="body1"
              sx={{ whiteSpace: "pre-wrap", lineHeight: 1.8 }}
            >
              {company.purpose || t("companyValues.empty")}
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}
